import Thunk from 'src/thunks/index';
import classesActions from 'src/store/model/classes';
import userClassesActions from 'src/store/model/class_user';
import archivedClassActions from 'src/store/model/archived_class';
import weekTopicActions from 'src/store/model/week_topic';
import { Data, List } from 'src/interfaces/general';
import app from 'src/store/ui/app';
import { unwrapResult } from '@reduxjs/toolkit';
import history from 'src/libs/history';
import { PATH } from 'src/constants/app';
import { Dispatch, RootState } from 'src/store';
import { isAdminSelector, isScopedTeacherSelector } from 'src/utils/helper';
import { fetchClassUserStatus } from 'src/thunks/class_user_status';

const classes = new Thunk();

export type Tab = 'all_courses' | 'my_courses';

export interface GetCourses extends SearchParams.ClassUser {
  tab?: Tab;
}

export const fetchAllClasses = classes.fetch<
  List<Model.Class>,
  SearchParams.Class
>('classes', {
  onSuccess: ({ dispatch, data }) => {
    dispatch(classesActions.updateEntities(data.data));
    dispatch(app.updatePagination(data.paginator));
  },
});

export const fetchUserClasses = classes.fetch<
  List<Model.ClassUser>,
  SearchParams.ClassUser
>('class_users', {
  onSuccess: ({ dispatch, data }) => {
    dispatch(userClassesActions.updateEntities(data.data));
    // dispatch(
    //   classesActions.updateEntities(
    //     data.data.flatMap((class_user) => class_user.class),
    //   ),
    // );
  },
});

export const fetchArchivedClasses = classes.fetch<
  List<Model.Class>,
  SearchParams.Class
>('classes', {
  onSuccess: ({ dispatch, data }) => {
    dispatch(archivedClassActions.updateEntities(data.data));
  },
});

export const fetchClassById = classes.fetchOne<Data<Model.Class>>('classes', {
  onSuccess: ({ dispatch, data }) => {
    dispatch(classesActions.updateEntity(data.data));
    dispatch(weekTopicActions.updateEntities(data.data.week_topics));
  },
});

export const fetchResources = classes.fetchResources<void, GetCourses>(
  'home',
  async (args, { getState, dispatch }) => {
    const { ...rest } = args;

    if (isScopedTeacherSelector(getState())) {
      const accessible: Model.Class[] = [];
      let page = 1;
      do {
        const response = await classes.get<List<Model.Class>>('classes', {
          params: { page, limit: 100, order_by: 'classes.created_at,desc' },
        });
        accessible.push(...response.data.data);
        page = response.data.paginator?.next_page || 0;
      } while (page);
      dispatch(classesActions.replaceEntities(accessible));
      await dispatch(
        fetchUserClasses({
          user_id: getState().ui.app.me?.id,
          limit: 100,
          ...rest,
        }),
      );
      await dispatch(fetchClassUserStatus({}));
      return;
    }

    await dispatch(
      fetchAllClasses({
        page: 1,
        limit: 10,
        is_published: true,
        order_by: 'classes.created_at,desc',
      }),
    );
    await dispatch(
      fetchUserClasses({
        page: 1,
        limit: 10,
        user_id: getState().ui.app.me?.id,
        ...rest,
      }),
    );
    await dispatch(
      fetchArchivedClasses({
        page: 1,
        limit: 10,
        is_published: false,
        show_to_iva_archived: true,
      }),
    );
    await dispatch(fetchClassUserStatus({}));
  },
);

export const updateClassUser = classes.update<
  Data<Model.ClassUser>,
  Partial<Model.ClassUser>
>('class_users', {
  onSuccess: ({ dispatch, data }) => {
    dispatch(userClassesActions.updateEntity(data.data));
  },
});

export const fetchAndCheckClassUser = async (
  class_id: string,
  dispatch: Dispatch,
  state: RootState,
  shouldCheckIsPaid: boolean = true,
) => {
  const class_users = await dispatch(
    fetchUserClasses({
      user_id: state.ui.app.me?.id,
      class_id: class_id,
    }),
  );

  const classUsers = unwrapResult(class_users).data;
  // A class can carry more than one enrolment row for the same user; the list
  // comes back newest-first, so index 0 is not necessarily the paid one.
  const classUser = classUsers.find((c) => c?.is_paid) || classUsers[0];
  const is_paid = classUser && classUser.is_paid;

  // Admins and teachers have no enrolment to check: their access is decided
  // by the API, per course.
  if (isAdminSelector(state) || isScopedTeacherSelector(state)) {
    return classUsers;
  }

  if (!is_paid && shouldCheckIsPaid) {
    history.push(PATH.NOT_FOUND);
    return;
  }

  return classUsers;
};
